import { Fragment, useMemo } from 'react';
import isEmpty from 'lodash/isEmpty';

import type { GetLessonTeachingAndLearningSequenceQuery } from './teaching-notes.graphql.generated';
import { Box } from 'approot/shared/design-system/box/box.style';
import {
  LEARNING_SEQUENCE_INTRODUCTION_CATEGORIES_LB,
  LEARNING_SEQUENCE_QUESTION_CATEGORIES_AB,
  LEARNING_SEQUENCE_CONCLUSION_CATEGORIES,
  LEARNING_SEQUENCE_CATEGORY_MATERIALS_NEEDED,
  LEARNING_SEQUENCE_CATEGORY_EXTRA_CONNECTIONS,
} from 'global/constants';
import TeachingNotesDetailRichtextView from './teaching-notes-detail-richtext-view';
import LessonMaterialsView from './lesson-materials/lesson-materials-view';
import ExtraConnectionsView from './extra-connections/extra-connections-view';
import type { Pluck } from 'lib/gql/pluck';

type Props = {
  categories:
    | typeof LEARNING_SEQUENCE_INTRODUCTION_CATEGORIES_LB
    | typeof LEARNING_SEQUENCE_QUESTION_CATEGORIES_AB
    | typeof LEARNING_SEQUENCE_CONCLUSION_CATEGORIES;
  notes?: Pluck<
    GetLessonTeachingAndLearningSequenceQuery,
    'getLessonTeachingAndLearningSequence'
  >[];
  displayExpanded?: boolean;
};

const TeachingNotesListView = ({
  notes,
  categories,
  displayExpanded,
}: Props) => {
  const firstNoteWithData = useMemo(
    () =>
      notes?.findIndex(
        note =>
          !isEmpty(note.data) ||
          note.lessonMaterials?.some(m => !!m.name.trim()) ||
          note.connections?.some(c => c.outcomes.length > 0)
      ),
    [notes]
  );

  return (
    <Box $marginBottom={24}>
      {notes?.map((note, index) => {
        const isLessonMaterials =
          note.category === LEARNING_SEQUENCE_CATEGORY_MATERIALS_NEEDED;
        const isExtraConnections =
          note.category === LEARNING_SEQUENCE_CATEGORY_EXTRA_CONNECTIONS;
        const title =
          categories.find(cat => cat.value === note.category)?.label || '';

        return (
          <Fragment key={note.id}>
            {isLessonMaterials ? (
              <LessonMaterialsView
                materials={note.lessonMaterials}
                title={title}
                displayExpanded={displayExpanded && index === firstNoteWithData}
              />
            ) : isExtraConnections ? (
              <ExtraConnectionsView
                connections={note.connections || []}
                title={title}
                displayExpanded={
                  !!(displayExpanded && index === firstNoteWithData)
                }
              />
            ) : (
              <TeachingNotesDetailRichtextView
                data={note.data}
                title={title}
                displayExpanded={displayExpanded && index === firstNoteWithData}
              />
            )}
          </Fragment>
        );
      })}
    </Box>
  );
};

export default TeachingNotesListView;
